import {z} from 'zod';

import {usageSchema} from '../common/UsageSchema';
import {extractDeliveryNoteDocumentMessageInputSchema} from '../openai/ExtractDeliveryNoteDocumentMessageInputSchema';
import {extractDeliveryNoteDocumentResultSchema} from '../openai/ExtractDeliveryNoteDocumentResult';
import {scanLicensePlateRequestSchema} from '../openai/ScanLicensePlateRequestSchema';
import {scannedLicensePlateSchema} from '../openai/ScannedLicensePlate';
import {scannedVINSchema} from '../openai/ScannedVIN';
import {scanVINRequestSchema} from '../openai/ScanVINRequestSchema';
import {
  EXTRACT_DELIVERY_NOTE_DOCUMENT,
  SCAN_LICENSE_PLATE,
  SCAN_VIN,
} from '../registry/UseCaseType';

/**
 * Schema describing the job status of a message
 */
export const messageStatusSchema = z.enum(['PENDING', 'PROCESSING', 'SUCCESS', 'FAILED']);

export type MessageStatus = z.infer<typeof messageStatusSchema>;

/**
 * Union schema for all possible message result types based on use case
 */
export const messageResultSchema = z.union([
  extractDeliveryNoteDocumentResultSchema,
  scannedVINSchema,
  scannedLicensePlateSchema,
]);

export type MessageResult = z.infer<typeof messageResultSchema>;

/**
 * Base message schema with common fields
 * This is the source of truth for the message structure used across frontend and backend
 * Matches the database structure where result is a JSON field containing {usage, result}
 */
const baseMessageSchema = z.object({
  id: z.string().uuid(),
  referenceId: z.string(),
  status: messageStatusSchema,
  serviceId: z.string(),
  tenantId: z.string(),
  userId: z.string(),
  requestId: z.string(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  error: z.string().nullable().optional(),
});

/**
 * Schema for scanVIN use case message
 */
const scanVINMessageSchema = baseMessageSchema.extend({
  useCaseType: z.literal(SCAN_VIN),
  input: scanVINRequestSchema,
  result: z
    .object({
      usage: usageSchema.nullable().optional(),
      result: scannedVINSchema.nullable().optional(),
    })
    .nullable()
    .optional(),
});

/**
 * Schema for scanLicensePlate use case message
 */
const scanLicensePlateMessageSchema = baseMessageSchema.extend({
  useCaseType: z.literal(SCAN_LICENSE_PLATE),
  input: scanLicensePlateRequestSchema,
  result: z
    .object({
      usage: usageSchema.nullable().optional(),
      result: scannedLicensePlateSchema.nullable().optional(),
    })
    .nullable()
    .optional(),
});

/**
 * Schema for extractDeliveryNoteDocument use case message
 */
const extractDeliveryNoteDocumentMessageSchema = baseMessageSchema.extend({
  useCaseType: z.literal(EXTRACT_DELIVERY_NOTE_DOCUMENT),
  input: extractDeliveryNoteDocumentMessageInputSchema,
  result: z
    .object({
      usage: usageSchema.nullable().optional(),
      result: extractDeliveryNoteDocumentResultSchema.nullable().optional(),
    })
    .nullable()
    .optional(),
});

/**
 * Discriminated union schema for message based on useCaseType
 * This represents the nested DB structure where:
 * - result is an object containing {usage, result}
 * - error, userId, and requestId are included at the top level
 */
export const messageSchema = z.discriminatedUnion('useCaseType', [
  scanVINMessageSchema,
  scanLicensePlateMessageSchema,
  extractDeliveryNoteDocumentMessageSchema,
]);

export type Message = z.infer<typeof messageSchema>;
